import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import BoltOsImmersivePanel from "../immersive-panel-launch/BoltOsImmersivePanel";
import {
  CatalogResponse,
  FALLBACK_CATALOG,
  FeatureDefinition,
} from "../immersive-panel-setup/ImmersivePanelSetupPage";

const AI_API_URL = process.env.REACT_APP_AI_API_URL || "http://127.0.0.1:8000";
// const AI_API_URL = "http://127.0.0.1:8000";

type RuntimeChatMessage = {
  author: string;
  text: string;
  avatar: string;
  timestamp?: number;
};

type RuntimeAnnouncement = {
  text: string;
  timestamp: number;
};

type RuntimeQuiz = {
  question: string;
  options: string[];
  timestamp: number;
};

type RuntimeSessionResponse = {
  session_id: string;
  experience_type: "event" | "stream";
  features: string[];
  answers: Record<string, unknown>;
  chat: RuntimeChatMessage[];
  announcements: RuntimeAnnouncement[];
  quizzes: RuntimeQuiz[];
  created_at: number;
  updated_at: number;
};

/* ---------- Shared UI ---------- */

const GlassCard: React.FC<
  React.PropsWithChildren<{
    title?: string;
    subtitle?: string;
    sx?: object;
  }>
> = ({ title, subtitle, sx, children }) => (
  <Box
    sx={{
      borderRadius: 4,
      border: `1px solid ${alpha("#FFFFFF", 0.1)}`,
      bgcolor: "rgba(15, 20, 32, 0.6)",
      backdropFilter: "blur(24px)",
      px: 3,
      py: 3,
      ...sx,
    }}
  >
    {(title || subtitle) && (
      <Box sx={{ mb: 2.5 }}>
        {title && (
          <Typography
            variant="h6"
            sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: 18, letterSpacing: 0.2 }}
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography
            variant="caption"
            sx={{ color: alpha("#FFFFFF", 0.5), fontSize: 13, lineHeight: 1.6, display: "block", mt: 0.5 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    )}
    {children}
  </Box>
);

/* ---------- Feature Tuner (matches setup page style) ---------- */

const FeatureTuner: React.FC<{
  features: FeatureDefinition[];
  activeIds: string[];
  onToggle: (featureId: string, active: boolean) => void;
}> = ({ features, activeIds, onToggle }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const basic = useMemo(() => features.filter((f) => f.tier === "basic"), [features]);
  const advanced = useMemo(() => features.filter((f) => f.tier === "advanced"), [features]);

  const Section: React.FC<{
    title: string;
    subtitle: string;
    items: FeatureDefinition[];
    gradient: string;
  }> = ({ title, subtitle, items, gradient }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ mb: 2, pb: 1.5, borderBottom: `2px solid ${alpha("#FFFFFF", 0.08)}` }}>
        <Typography
          variant="h6"
          sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: 16, mb: 0.5, letterSpacing: 0.3 }}
        >
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: alpha("#FFFFFF", 0.5), fontSize: 13 }}>
          {subtitle}
        </Typography>
      </Box>

      <Stack spacing={1}>
        {items.map((feature) => {
          const active = activeIds.includes(feature.id);
          const expanded = expandedId === feature.id;
          return (
            <Box
              key={feature.id}
              sx={{
                position: "relative",
                borderRadius: 2.5,
                overflow: "hidden",
                border: `1px solid ${active ? alpha("#6366F1", 0.4) : alpha("#FFFFFF", 0.06)}`,
                bgcolor: active ? alpha("#6366F1", 0.04) : alpha("#FFFFFF", 0.01),
                transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  bgcolor: active ? alpha("#6366F1", 0.08) : alpha("#FFFFFF", 0.03),
                  borderColor: active ? alpha("#6366F1", 0.5) : alpha("#FFFFFF", 0.12),
                  transform: "translateX(2px)",
                },
              }}
            >
              {active && (
                <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: gradient }} />
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  pl: active ? 2.5 : 2,
                }}
              >
                <Box sx={{ flex: 1, mr: 2, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "#FFFFFF", fontWeight: 600, fontSize: 14, lineHeight: 1.4 }}
                    >
                      {feature.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={feature.tier === "advanced" ? "Premium" : "Core"}
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        bgcolor:
                          feature.tier === "advanced" ? alpha("#8B5CF6", 0.12) : alpha("#10B981", 0.12),
                        color: feature.tier === "advanced" ? "#C4B5FD" : "#6EE7B7",
                        border: `1px solid ${
                          feature.tier === "advanced" ? alpha("#8B5CF6", 0.3) : alpha("#10B981", 0.3)
                        }`,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                    <Tooltip title={expanded ? "Click to collapse" : "Click to expand details"} placement="top">
                      <InfoOutlinedIcon
                        onClick={() => setExpandedId(expanded ? null : feature.id)}
                        sx={{
                          color: alpha("#FFFFFF", 0.35),
                          fontSize: 16,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": { color: alpha("#FFFFFF", 0.7), transform: "scale(1.1)" },
                        }}
                      />
                    </Tooltip>
                  </Stack>

                  <Collapse in={expanded} timeout={300}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: alpha("#FFFFFF", 0.55),
                        fontSize: 12.5,
                        lineHeight: 1.6,
                        display: "block",
                        mt: 1,
                        pr: 1,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Collapse>
                </Box>

                <Switch
                  checked={active}
                  onChange={(e) => onToggle(feature.id, e.target.checked)}
                  sx={{
                    flexShrink: 0,
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#6366F1",
                      "& + .MuiSwitch-track": { backgroundColor: "#6366F1", opacity: 0.5 },
                    },
                    "& .MuiSwitch-track": { backgroundColor: alpha("#FFFFFF", 0.15) },
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );

  return (
    <Box>
      <Section
        title="Core Experiences"
        subtitle="Essential features to engage your community"
        items={basic}
        gradient="linear-gradient(180deg, #10B981 0%, #059669 100%)"
      />
      <Section
        title="Premium Intelligence"
        subtitle="Advanced AI capabilities for deeper engagement"
        items={advanced}
        gradient="linear-gradient(180deg, #8B5CF6 0%, #7C3AED 100%)"
      />
    </Box>
  );
};

/* ---------- Page ---------- */

const ImmersivePanelAdminPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionKey = searchParams.get("session") || "";

  const [catalog, setCatalog] = useState<CatalogResponse>(FALLBACK_CATALOG);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [session, setSession] = useState<RuntimeSessionResponse | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const [activeFeatures, setActiveFeatures] = useState<string[]>([]);
  const [announcements, setAnnouncements] = useState<RuntimeAnnouncement[]>([]);
  const [quizzes, setQuizzes] = useState<RuntimeQuiz[]>([]);

  const [announcementText, setAnnouncementText] = useState("");
  const [quizQuestion, setQuizQuestion] = useState("");
  const [quizOptions, setQuizOptions] = useState<string[]>(["", "", "", ""]);
  const [quizMultipleChoice, setQuizMultipleChoice] = useState(true); // visual only

  /* Catalog */
  useEffect(() => {
    let mounted = true;
    setCatalogLoading(true);
    axios
      .get<CatalogResponse>(`${AI_API_URL}/immersive-setup/catalog`)
      .then((response) => {
        if (mounted) {
          setCatalog(response.data);
          setCatalogError(null);
        }
      })
      .catch(() => {
        if (mounted) {
          setCatalog(FALLBACK_CATALOG);
          setCatalogError("Using default catalog while we reconnect.");
        }
      })
      .finally(() => {
        if (mounted) setCatalogLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  /* Session */
  const loadSession = async (key: string) => {
    if (!key) {
      setSession(null);
      return;
    }
    setSessionLoading(true);
    try {
      const response = await axios.get<RuntimeSessionResponse>(
        `${AI_API_URL}/immersive-runtime/session/${key}`
      );
      setSession(response.data);
      setActiveFeatures(response.data.features);
      setAnnouncements(response.data.announcements || []);
      setQuizzes(response.data.quizzes || []);
      setSessionError(null);
    } catch {
      setSession(null);
      setActiveFeatures([]);
      setAnnouncements([]);
      setQuizzes([]);
      setSessionError("Unable to load session details.");
    } finally {
      setSessionLoading(false);
    }
  };

  useEffect(() => {
    if (sessionKey) {
      loadSession(sessionKey);
    } else {
      setSession(null);
      setActiveFeatures([]);
      setAnnouncements([]);
      setQuizzes([]);
    }
  }, [sessionKey]);

  /* Live updates */
  useEffect(() => {
    if (!sessionKey) return () => {};

    const url = `${AI_API_URL.replace(/^http/, "ws")}/immersive-runtime/session/${sessionKey}/events`;
    const socket = new WebSocket(url);

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        switch (payload.type) {
          case "session_snapshot":
            if (Array.isArray(payload.features)) setActiveFeatures(payload.features);
            if (Array.isArray(payload.announcements)) setAnnouncements(payload.announcements);
            if (Array.isArray(payload.quizzes)) setQuizzes(payload.quizzes);
            break;
          case "features":
            if (Array.isArray(payload.features)) setActiveFeatures(payload.features);
            break;
          case "announcement":
            if (payload.announcement) setAnnouncements((prev) => [...prev, payload.announcement]);
            break;
          case "quiz":
            if (payload.quiz) setQuizzes((prev) => [...prev, payload.quiz]);
            break;
          default:
            break;
        }
      } catch (error) {
        console.warn("Failed to parse admin event", error);
      }
    };

    socket.onerror = () => {
      console.warn("Admin websocket error");
    };

    return () => {
      socket.close();
    };
  }, [sessionKey]);

  const availableFeatures: FeatureDefinition[] = useMemo(() => {
    if (!session) return [];
    return session.experience_type === "event"
      ? catalog.event
      : catalog.stream;
  }, [catalog, session]);

  /* Actions */
  const handleToggleFeature = async (featureId: string, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...activeFeatures, featureId]))
      : activeFeatures.filter((id) => id !== featureId);
    setActiveFeatures(next);
    try {
      await axios.patch(`${AI_API_URL}/immersive-runtime/session/${sessionKey}/features`, {
        features: next,
      });
    } catch {
      loadSession(sessionKey);
    }
  };

  const handleSendAnnouncement = async () => {
    const text = announcementText.trim();
    if (!text || !sessionKey) return;
    try {
      await axios.post(`${AI_API_URL}/immersive-runtime/session/${sessionKey}/announcement`, { text });
      setAnnouncementText("");
    } catch {
      // no-op
    }
  };

  const handleSendQuiz = async () => {
    if (!sessionKey) return;
    const trimmedQuestion = quizQuestion.trim();
    const trimmedOptions = quizOptions.map((o) => o.trim());
    if (!trimmedQuestion || trimmedOptions.some((o) => !o)) return;
    try {
      await axios.post(`${AI_API_URL}/immersive-runtime/session/${sessionKey}/quiz`, {
        question: trimmedQuestion,
        options: trimmedOptions,
      });
      setQuizQuestion("");
      setQuizOptions(["", "", "", ""]);
    } catch {
      // no-op
    }
  };

  /* Screens */

  if (!sessionKey) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          bgcolor: "#050714",
          color: "#FFFFFF",
          fontSize: 18,
        }}
      >
        Provide a session key (e.g. /immersive-panel-admin?session=SESSION_ID)
      </Box>
    );
  }

  if (catalogLoading || sessionLoading || !session) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          bgcolor: "#050714",
        }}
      >
        <CircularProgress sx={{ color: "#8B5CF6" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "radial-gradient(circle at top, #10173A 0%, #090D21 60%, #050714 100%)",
        p: { xs: 2, md: 4 },
        color: "#FFFFFF",
      }}
    >
      <Stack
        spacing={2}
        sx={{ maxWidth: 1320, mx: "auto", alignItems: "center" }} // center children horizontally
      >
        <Box sx={{ textAlign: "center" }}>  {/* center the texts */}
          <Typography
            variant="overline"
            sx={{ letterSpacing: 2, color: alpha("#FFFFFF", 0.6) }}
          >
            BoltOS Immersive Panel
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, mt: 0.25 }}
          >
            Admin Control Panel
          </Typography>
          {catalogError && (
            <Typography variant="body2" sx={{ color: alpha("#FDE68A", 0.9), mt: 0.75 }}>
              {catalogError}
            </Typography>
          )}
          {sessionError && (
            <Typography variant="body2" sx={{ color: alpha("#FDE68A", 0.9), mt: 0.5 }}>
              {sessionError}
            </Typography>
          )}
        </Box>

        {/* ROW 1: Preview (left) + Feature tuner (right) */}
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={6}>
            <BoltOsImmersivePanel
              features={availableFeatures}
              activeIds={activeFeatures}
              sessionKey={sessionKey}
              initialChat={session.chat}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <GlassCard
              title="Customize Features"
              subtitle="Toggle modules on/off for this session. Changes apply live."
              sx={{
                height: "100%",
                maxHeight: { xs: 700, lg: 900 },
                overflow: "auto",
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-track": { background: alpha("#FFFFFF", 0.02), borderRadius: 3 },
                "&::-webkit-scrollbar-thumb": {
                  background: alpha("#6366F1", 0.3),
                  borderRadius: 3,
                  "&:hover": { background: alpha("#6366F1", 0.5) },
                },
              }}
            >
              <FeatureTuner
                features={availableFeatures}
                activeIds={activeFeatures}
                onToggle={handleToggleFeature}
              />
              <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${alpha("#FFFFFF", 0.06)}` }}>
                <Typography
                  variant="caption"
                  sx={{ color: alpha("#FFFFFF", 0.45), fontSize: 12, display: "block" }}
                >
                  💡 Tip: Premium features are labeled “Premium” and include advanced AI.
                </Typography>
              </Box>
            </GlassCard>
          </Grid>
        </Grid>

        {/* ROW 2: Announcements + Quiz BELOW the preview (uses full width) */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <GlassCard title="Send Announcement" subtitle="Broadcast quick reminders or updates">
              <TextField
                multiline
                minRows={3}
                value={announcementText}
                onChange={(e) => setAnnouncementText(e.target.value)}
                placeholder="Share a logistics update, a sponsor callout, or next segment teaser."
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    bgcolor: alpha("#FFFFFF", 0.04),
                    color: "#FFFFFF",
                    borderRadius: 2,
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha("#FFFFFF", 0.1) },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha("#FFFFFF", 0.2) },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6366F1" },
                }}
              />
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSendAnnouncement}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 600,
                    px: 2.5,
                    background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
                    boxShadow: `0 8px 22px ${alpha("#6366F1", 0.35)}`,
                    "&:hover": {
                      background: "linear-gradient(135deg, #5558E3 0%, #7C3AED 100%)",
                      boxShadow: `0 12px 30px ${alpha("#6366F1", 0.45)}`,
                    },
                  }}
                >
                  Push Announcement
                </Button>
                <Typography variant="caption" sx={{ color: alpha("#FFFFFF", 0.5) }}>
                  Recent
                </Typography>
              </Stack>

              <Stack spacing={1.25} sx={{ mt: 2 }}>
                {announcements
                  .slice()
                  .reverse()
                  .slice(0, 6)
                  .map((a) => (
                    <Box
                      key={a.timestamp}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha("#8B5CF6", 0.08),
                        border: `1px solid ${alpha("#8B5CF6", 0.2)}`,
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, color: "#FFFFFF", fontSize: 13 }}>
                        {new Date(a.timestamp * 1000).toLocaleTimeString()}
                      </Typography>
                      <Typography sx={{ color: alpha("#FFFFFF", 0.85), fontSize: 14, mt: 0.5 }}>
                        {a.text}
                      </Typography>
                    </Box>
                  ))}
              </Stack>
            </GlassCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <GlassCard title="Launch Quiz" subtitle="Keep the room energized with quick polls">
              <TextField
                label="Question"
                value={quizQuestion}
                onChange={(e) => setQuizQuestion(e.target.value)}
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    bgcolor: alpha("#FFFFFF", 0.04),
                    color: "#FFFFFF",
                    borderRadius: 2,
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha("#FFFFFF", 0.1) },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha("#FFFFFF", 0.2) },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6366F1" },
                }}
              />
              <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                {quizOptions.map((option, index) => (
                  <TextField
                    key={index}
                    label={`Option ${index + 1}`}
                    value={option}
                    onChange={(e) => {
                      const next = [...quizOptions];
                      next[index] = e.target.value;
                      setQuizOptions(next);
                    }}
                    fullWidth
                    sx={{
                      "& .MuiInputBase-root": {
                        bgcolor: alpha("#FFFFFF", 0.04),
                        color: "#FFFFFF",
                        borderRadius: 2,
                      },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha("#FFFFFF", 0.1) },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha("#FFFFFF", 0.2) },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6366F1" },
                    }}
                  />
                ))}
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={quizMultipleChoice}
                    onChange={(e) => setQuizMultipleChoice(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#10B981",
                        "& + .MuiSwitch-track": { backgroundColor: "#10B981", opacity: 0.5 },
                      },
                      "& .MuiSwitch-track": { backgroundColor: alpha("#FFFFFF", 0.15) },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: alpha("#FFFFFF", 0.8) }}>
                    Allow multiple submissions
                  </Typography>
                }
                sx={{ mt: 1.5 }}
              />
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={handleSendQuiz}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 600,
                    px: 2.5,
                    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                    boxShadow: `0 8px 22px ${alpha("#10B981", 0.35)}`,
                    "&:hover": {
                      background: "linear-gradient(135deg, #0EA972 0%, #047857 100%)",
                      boxShadow: `0 12px 30px ${alpha("#10B981", 0.45)}`,
                    },
                  }}
                >
                  Publish Quiz
                </Button>
                <Typography variant="caption" sx={{ color: alpha("#FFFFFF", 0.5) }}>
                  Recent
                </Typography>
              </Stack>

              <Stack spacing={1.25} sx={{ mt: 2 }}>
                {quizzes
                  .slice()
                  .reverse()
                  .slice(0, 6)
                  .map((quiz) => (
                    <Box
                      key={quiz.timestamp}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha("#34D399", 0.08),
                        border: `1px solid ${alpha("#34D399", 0.2)}`,
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, color: "#FFFFFF" }}>{quiz.question}</Typography>
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        {quiz.options.map((option, idx) => (
                          <Typography key={idx} sx={{ color: alpha("#FFFFFF", 0.85), fontSize: 14 }}>
                            • {option}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  ))}
              </Stack>
            </GlassCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default ImmersivePanelAdminPage;
